/**
 * The time grid (design 5A): time runs down 08:00–22:00 behind a scroll;
 * columns are days (week) or practitioners / rooms (day). A block shows its
 * time and the patient, plus the practitioner where the column does not
 * already say who (week, room lens) — the service is the block's colour,
 * named in the legend (Product 2026-10-16: no service, mode or room text);
 * cancelled / no-show keep their place, faded and dashed, labelled in
 * words; Delivered derives from the session record. Zones tint outside
 * working hours and hatch absences — informational only.
 */

import { useIntl } from 'react-intl';
import s from './scheduling.module.css';
import type { CalendarItem } from './bookingRules';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatTime } from '@/core/calendar/format';
import {
  GRID_END_MINUTE,
  GRID_START_MINUTE,
  hhmm,
  isGhost,
  layoutDay,
  serviceGroup,
  type ServiceGroup,
  type Zone,
} from './calendarMath';

export const HOUR_PX = 64;
const MIN_PX = HOUR_PX / 60;

export const GROUP_STYLE: Record<ServiceGroup, { bg: string; border: string }> = {
  speech: { bg: 'var(--zc-sage-bg)', border: 'var(--zc-sage-dot)' },
  ot: { bg: 'var(--zc-warn-bg)', border: 'var(--zc-warn-dot)' },
  assess: { bg: 'var(--zc-info-bg)', border: 'var(--zc-info-dot)' },
  coach: { bg: 'var(--zc-warm)', border: 'var(--zc-line-strong)' },
  other: { bg: 'var(--zc-card)', border: 'var(--zc-line-strong)' },
};

export interface GridColumn {
  id: string;
  label: string;
  sub?: string;
  dateISO: string;
  today?: boolean;
  /**
   * ZOY-680: official holiday names for this day — presentation only, shown
   * under the header. Never a zone: it changes no slot, block or availability.
   */
  holidays?: readonly string[];
  zones: Zone[];
  items: CalendarItem[];
  /** Slot clicks carry this practitioner / room to the new-booking form. */
  practitionerUid?: string;
  roomId?: string;
}

export function CalendarGrid(props: {
  columns: GridColumn[];
  tz: string;
  patientName: (b: CalendarItem) => string;
  staffName: (uid: string) => string;
  roomName: (id: string | null) => string | null;
  showPractitioner: boolean;
  onBooking: (b: CalendarItem) => void;
  onSlot?: (col: GridColumn, minuteOfDay: number) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const { locale } = useLocale();
  const hours = Array.from(
    { length: (GRID_END_MINUTE - GRID_START_MINUTE) / 60 },
    (_, i) => GRID_START_MINUTE + i * 60,
  );
  const bodyH = (GRID_END_MINUTE - GRID_START_MINUTE) * MIN_PX;
  const time = (iso: string) => formatTime(iso, { locale, timezone: props.tz });

  return (
    <div className={s.gridWrap}>
      <div
        className={s.grid}
        style={{ gridTemplateColumns: `56px repeat(${props.columns.length}, minmax(180px, 1fr))` }}
      >
        <div className={s.axis}>
          <div className={s.colHead} />
          <div className={s.body} style={{ blockSize: bodyH }}>
            {hours.map((m) => (
              <span
                key={m}
                className={s.axisLabel}
                style={{ insetBlockStart: (m - GRID_START_MINUTE) * MIN_PX }}
              >
                {/* ZOY-638: both halves of the clock label in the reader's digits. */}
                {intl.formatNumber(Math.floor(m / 60), { minimumIntegerDigits: 2 })}:
                {intl.formatNumber(0, { minimumIntegerDigits: 2 })}
              </span>
            ))}
          </div>
        </div>
        {props.columns.map((col) => {
          const placed = layoutDay(col.items, col.dateISO, props.tz);
          return (
            <div key={col.id} className={s.col}>
              <div
                className={[
                  s.colHead,
                  col.today ? s.colHeadToday : '',
                  col.holidays?.length ? s.colHeadHolidayDay : '',
                ].join(' ')}
              >
                {col.label}
                {col.sub && <span className={s.colHeadSub}>{col.sub}</span>}
                {col.holidays && col.holidays.length > 0 && (
                  <span
                    className={s.colHeadHoliday}
                    title={col.holidays.join('\n')}
                    aria-label={`${t('cal.holiday.label')}: ${col.holidays.join('، ')}`}
                  >
                    {col.holidays.join(' · ')}
                  </span>
                )}
              </div>
              <div className={s.body} style={{ blockSize: bodyH }}>
                {hours.map((m) => (
                  <div
                    key={m}
                    className={s.hourLine}
                    style={{ insetBlockStart: (m - GRID_START_MINUTE) * MIN_PX }}
                  />
                ))}
                {col.zones.map((z, i) => (
                  <div
                    key={`z${i}`}
                    className={z.kind === 'outside' ? s.zoneOutside : s.zoneAbsence}
                    style={{
                      insetBlockStart: Math.max(0, z.top) * MIN_PX,
                      blockSize: Math.max(0, z.height) * MIN_PX,
                    }}
                  >
                    {z.kind === 'absence'
                      ? (z.label ?? t('cal.absence'))
                      : z.height >= 60
                        ? t('cal.outside')
                        : ''}
                  </div>
                ))}
                {props.onSlot &&
                  hours.flatMap((m) =>
                    [0, 30].map((half) => (
                      <button
                        key={`${m}-${half}`}
                        type="button"
                        className={s.slot}
                        aria-label={`${col.label} ${hhmm(m + half)}`}
                        style={{
                          insetBlockStart: (m + half - GRID_START_MINUTE) * MIN_PX,
                          blockSize: 30 * MIN_PX,
                        }}
                        onClick={() => props.onSlot?.(col, m + half)}
                      />
                    )),
                  )}
                {placed.map((p) => {
                  const b = p.item;
                  const g = GROUP_STYLE[serviceGroup(b.service.name)];
                  const ghost = isGhost(b);
                  const width = 100 / p.lanes;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className={[s.block, ghost ? s.ghost : ''].join(' ')}
                      style={{
                        insetBlockStart: Math.max(0, p.top) * MIN_PX,
                        blockSize: Math.max(26, p.height * MIN_PX - 2),
                        insetInlineStart: `${p.lane * width}%`,
                        inlineSize: `calc(${width}% - 4px)`,
                        background: g.bg,
                        borderColor: g.border,
                      }}
                      onClick={() => props.onBooking(b)}
                      aria-label={`${time(b.startsAt)} ${b.group ? t('grp.block') : props.patientName(b)} ${b.service.name}`}
                    >
                      <span className={s.blockTime}>
                        {time(b.startsAt)}–{time(b.endsAt)}
                        {ghost && <> · {t(`bk.status.${b.status}`)}</>}
                        {b.executionStatus === 'in_progress' && <> · {t('bk.exec.in_progress')}</>}
                        {b.delivered && <> · ✓ {t('bk.delivered')}</>}
                      </span>
                      {/* ZOY-685: a group occurrence is ONE block — its attendees are a count. */}
                      <span className={s.blockName}>
                        {b.group
                          ? `${t('grp.block')} · ${t('grp.count', { n: b.group.participantCount })}`
                          : props.patientName(b)}
                      </span>
                      {props.showPractitioner && (
                        <span className={s.blockMeta}>{props.staffName(b.practitionerUid)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Legend() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className={s.legend} aria-label={t('cal.legendTitle')}>
        {(['speech', 'ot', 'assess', 'coach', 'other'] as const).map((g) => (
          <span key={g}>
            <span
              className={s.swatch}
              style={{ background: GROUP_STYLE[g].bg, borderColor: GROUP_STYLE[g].border }}
            />
            {t(`cal.group.${g}`)}
          </span>
        ))}
        <span>
          <span
            className={s.swatch}
            style={{
              background: 'var(--zc-warm)',
              borderColor: 'var(--zc-line-strong)',
              borderStyle: 'dashed',
            }}
          />
          {t('cal.legendGhost')}
        </span>
        <span>
          <span
            className={s.swatch}
            style={{ background: 'var(--zc-warm)', borderColor: 'var(--zc-line)', opacity: 0.7 }}
          />
          {t('cal.legendOutside')}
        </span>
        <span>
          <span
            className={s.swatch}
            style={{
              background:
                'repeating-linear-gradient(45deg, var(--zc-warm) 0 3px, var(--zc-line) 3px 6px)',
              borderColor: 'var(--zc-line-strong)',
              borderStyle: 'dashed',
            }}
          />
          {t('cal.legendAbsence')}
        </span>
      </div>
    </div>
  );
}
